/**
 * Strict CSV date parsing: rejects ambiguous JS/ dayjs parses (e.g. 11/30/20224 → year 20224).
 * Accepts only YYYY-MM-DD or M/D/YYYY with exactly a 4-digit year; calendar must be valid.
 */
import dayjs from "dayjs";
import { isCsvPlaceholderValue } from "./csvNumber";

export const CSV_DATE_YEAR_MIN = 1900;
export const CSV_DATE_YEAR_MAX = 2100;

/**
 * @param {unknown} val - non-empty string expected
 * @returns {{ ok: true, iso: string } | { ok: false, error: string }}
 */
export function parseCsvDateToIso(val) {
  if (val == null) return { ok: false, error: "Date is required" };
  const t = String(val).trim();
  if (!t) return { ok: false, error: "Date is required" };

  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
    const d = dayjs(t, "YYYY-MM-DD", true);
    if (!d.isValid()) return { ok: false, error: "Invalid date" };
    const y = d.year();
    if (y < CSV_DATE_YEAR_MIN || y > CSV_DATE_YEAR_MAX) {
      return { ok: false, error: `Year must be ${CSV_DATE_YEAR_MIN}–${CSV_DATE_YEAR_MAX}` };
    }
    return { ok: true, iso: t };
  }

  const m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) {
    return {
      ok: false,
      error: `Use YYYY-MM-DD or M/D/YYYY with a 4-digit year (${CSV_DATE_YEAR_MIN}–${CSV_DATE_YEAR_MAX})`,
    };
  }
  const month = parseInt(m[1], 10);
  const day = parseInt(m[2], 10);
  const year = parseInt(m[3], 10);
  const padded = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const d = dayjs(padded, "YYYY-MM-DD", true);
  if (!d.isValid()) return { ok: false, error: "Invalid date" };
  if (d.year() !== year || d.month() + 1 !== month || d.date() !== day) {
    return { ok: false, error: "Invalid date" };
  }
  if (year < CSV_DATE_YEAR_MIN || year > CSV_DATE_YEAR_MAX) {
    return { ok: false, error: `Year must be ${CSV_DATE_YEAR_MIN}–${CSV_DATE_YEAR_MAX}` };
  }
  return { ok: true, iso: d.format("YYYY-MM-DD") };
}

/**
 * Empty or whitespace → valid with iso "".
 * @returns {{ ok: true, iso: string } | { ok: false, error: string }}
 */
export function parseCsvDateToIsoOptional(val) {
  if (val == null || String(val).trim() === "" || isCsvPlaceholderValue(val)) return { ok: true, iso: "" };
  return parseCsvDateToIso(val);
}
